/**
 * apps/web :: lib/entitlement-api
 *
 * Typed wrappers around the `/api/entitlements/*` surface (BACK-01,
 * ROADS-445). Field names mirror the backend wire shape verbatim
 * (`@SerialName` snake_case) — same convention as the rest of the
 * frontend's API libs.
 *
 * Endpoints:
 *   - GET /api/entitlements/me                 → 200 {entitlements,now_ms}
 *   - GET /api/entitlements/me/check?tier=...  → 200 {has_access, ...}
 *
 * Auth: requires the `rb_session` cookie; `apiFetch` always sends
 * credentials. 401s bubble up as `ApiError`.
 */
import { apiFetch } from "@/lib/api";

/** Paid product tiers. Mirrors `EntitlementTier.wire` from kmp-shared-core. */
export type EntitlementTier =
  | "class_c"
  | "cdl"
  | "tourist_in"
  | "tourist_out"
  | "motorcycle";

/** Entitlement provenance. Mirrors `EntitlementSource.wire`. */
export type EntitlementSource = "trial" | "purchase" | "comp";

/** A single row in the entitlements list. */
export interface Entitlement {
  id: string;
  user_id: string;
  tier: EntitlementTier;
  state_code: string | null;
  country_code: string | null;
  expires_at_ms: number;
  source: EntitlementSource;
  created_at_ms: number;
  updated_at_ms: number;
  /**
   * Stripe subscription id (`sub_*`) for recurring tiers. Null for
   * one-time pulse purchases (tourist_in / tourist_out) and legacy rows
   * that pre-date FE-03 (ROADS-445). The FE renders cancel/reactivate
   * controls only when this is non-null.
   *
   * Optional in TS so pre-FE-03 fixtures still typecheck. Backend always
   * emits the field (kotlinx-serialization encodes default = null).
   */
  stripe_subscription_id?: string | null;
  /**
   * True when the user has scheduled a cancel-at-period-end through
   * Stripe; the FE shows "Cancellation scheduled — access ends [date]"
   * and a "Reactivate" button while this is true. Always false for
   * one-time purchases.
   *
   * Optional in TS so pre-FE-03 fixtures still typecheck. Backend always
   * emits the field (kotlinx-serialization encodes default = false).
   */
  cancel_at_period_end?: boolean;
}

/** Response body for `GET /api/entitlements/me`. */
export interface EntitlementsResponse {
  entitlements: Entitlement[];
  now_ms: number;
}

/** Response body for `GET /api/entitlements/me/check`. */
export interface EntitlementCheckResponse {
  has_access: boolean;
  tier: EntitlementTier;
  state_code: string | null;
  country_code: string | null;
  now_ms: number;
}

/**
 * Fetch every active entitlement for the authenticated user. Used by the
 * paywall renew-existing-tier path (when the user has expired rows the
 * picker collapses to a single "Renew $X" CTA).
 */
export async function listMyEntitlements(): Promise<EntitlementsResponse> {
  return apiFetch<EntitlementsResponse>("/api/entitlements/me");
}

/**
 * Server-side has-access check for a (tier, state, country) tuple. The
 * route gates use this to decide whether to render content or the
 * paywall.
 */
export async function checkAccess(params: {
  tier: EntitlementTier;
  state?: string | null;
  country?: string | null;
}): Promise<EntitlementCheckResponse> {
  const search = new URLSearchParams();
  search.set("tier", params.tier);
  if (params.state) search.set("state", params.state);
  if (params.country) search.set("country", params.country);
  return apiFetch<EntitlementCheckResponse>(
    `/api/entitlements/me/check?${search.toString()}`,
  );
}

/**
 * Response body for `POST /api/subscriptions/{id}/cancel` and
 * `POST /api/subscriptions/{id}/reactivate`. Mirrors the backend
 * SubscriptionMutationResponse — single field that contains the
 * refreshed entitlement row.
 */
export interface SubscriptionMutationResponse {
  entitlement: Entitlement;
}

/**
 * Schedule a cancel-at-period-end on the named entitlement's Stripe
 * subscription. The user keeps access until expires_at; Stripe fires
 * `customer.subscription.deleted` then and BACK-02 shortens entitlement
 * expiry. Throws ApiError on 4xx/5xx — callers map status codes:
 *   - 401 -> redirect to /login
 *   - 403 / 404 -> "We couldn't find that entitlement"
 *   - 409 -> "Tourist & outbound passes are one-time purchases"
 *   - 503 -> "Try again in a minute"
 */
export async function cancelSubscription(
  entitlementId: string,
): Promise<SubscriptionMutationResponse> {
  return apiFetch<SubscriptionMutationResponse>(
    `/api/subscriptions/${encodeURIComponent(entitlementId)}/cancel`,
    { method: "POST" },
  );
}

/**
 * Undo a pending cancel-at-period-end on the named entitlement's Stripe
 * subscription. The subscription continues billing. Same status-code
 * map as cancelSubscription.
 */
export async function reactivateSubscription(
  entitlementId: string,
): Promise<SubscriptionMutationResponse> {
  return apiFetch<SubscriptionMutationResponse>(
    `/api/subscriptions/${encodeURIComponent(entitlementId)}/reactivate`,
    { method: "POST" },
  );
}
